import { createUrl } from "@/redux/api";
import {
  DataBlockResponse,
  DataBlockListResponse,
  Tags,
  TagType,
  ChoiceOptionsQueryParams,
  DataTemplatesQueryParams,
  DataTemplateResponse,
  ChoiceOptionResponse,
  DataBlockRequest,
  DataBlockRequestQueryParams,
  DataBlocksQueryParams,
  DataBlockVersionResponse,
  DataBlockVersionsQueryParams,
  DataBlockChangesetResponse,
  DataBlockHistoryQueryParams,
} from "./types";
import { isPaginatedResponse } from "@/redux/typeGuards";
import { BuilderType, PaginatedResponse } from "@/redux/types";
import { DataBlockFormFields } from "@/domains/datablocks/types";

const DATABLOCKS_URL = "/datablocks/blocks/";
const DATABLOCK_REQUESTS_URL = "/datablocks/requests/";
const DATA_TEMPLATES_URL = "/datablocks/templates/";
const DATACHOICES_URL = "/datablocks/choices/";

const dataBlocksService = (builder: BuilderType<Tags>) => ({
  getDataBlocks: builder.query<
    DataBlockListResponse[] | PaginatedResponse<DataBlockListResponse>,
    DataBlocksQueryParams
  >({
    query: (queryParams) => ({
      url: createUrl(DATABLOCKS_URL, queryParams),
      method: "GET",
    }),
    providesTags: (result): TagType[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as DataBlockListResponse[]
            ).map(
              (i): TagType => ({
                type: "DataBlock",
                id: i.id,
              }),
            ),
            { type: "DataBlocks" },
          ]
        : [{ type: "DataBlocks" }],
  }),
  createDataBlock: builder.mutation<DataBlockResponse, DataBlockFormFields>({
    query: (data) => ({
      url: DATABLOCKS_URL,
      method: "POST",
      data,
    }),
    invalidatesTags: [
      { type: "DataBlocks" },
      { type: "DataBlockRequests" },
      { type: "DataFiles" },
      { type: "EvidenceAnalytics" },
    ],
  }),
  getDataBlockById: builder.query<
    DataBlockResponse,
    number | string | undefined
  >({
    query: (id) => ({
      url: `${DATABLOCKS_URL}${id}/`,
      method: "GET",
    }),
    providesTags: (result): TagType[] =>
      result
        ? [{ type: "DataBlock", id: result.id }, { type: "DataBlocks" }]
        : [{ type: "DataBlocks" }],
  }),
  updateDataBlock: builder.mutation({
    query: ({ id, data }) => ({
      url: `${DATABLOCKS_URL}${id}/`,
      method: "PUT",
      data: data,
    }),
    invalidatesTags: [
      { type: "DataBlocks" },
      { type: "DataFiles" },
      { type: "EvidenceAnalytics" },
      { type: "DataBlockHistories" },
    ],
  }),
  patchDataBlock: builder.mutation({
    query: ({ id, data }) => ({
      url: `${DATABLOCKS_URL}${id}/`,
      method: "PATCH",
      data: data,
    }),
    invalidatesTags: [
      { type: "DataBlocks" },
      { type: "DataFiles" },
      { type: "EvidenceAnalytics" },
      { type: "DataBlockHistories" },
    ],
  }),
  deleteDataBlock: builder.mutation({
    query: (id) => ({
      url: `${DATABLOCKS_URL}${id}/`,
      method: "DELETE",
    }),
    invalidatesTags: [
      { type: "DataBlocks" },
      { type: "DataFiles" },
      { type: "EvidenceAnalytics" },
      { type: "DataBlockHistories" },
    ],
  }),
  getDataBlockVersions: builder.query<
    DataBlockVersionResponse[] | PaginatedResponse<DataBlockVersionResponse>,
    DataBlockVersionsQueryParams
  >({
    query: (data) => ({
      url: createUrl(`${DATABLOCKS_URL}${data.id}/versions/`, data),
      method: "GET",
    }),
    providesTags: (result, _, args): TagType[] =>
      result
        ? [
            { type: "DataBlockVersion", id: args.id },
            { type: "DataBlockVersions" },
          ]
        : [{ type: "DataBlockVersions" }],
  }),
  getDataBlockHistory: builder.query<
    | DataBlockChangesetResponse[]
    | PaginatedResponse<DataBlockChangesetResponse>,
    { id: number; filters: DataBlockHistoryQueryParams }
  >({
    query: ({ id, filters }) => ({
      url: createUrl(`${DATABLOCKS_URL}${id}/history/`, filters),
      method: "GET",
    }),
    providesTags: (result, _, args): TagType[] =>
      result
        ? [
            { type: "DataBlockHistory", id: args.id },
            { type: "DataBlockHistories" },
          ]
        : [{ type: "DataBlockHistories" }],
  }),
  incrementDataBlockViews: builder.query({
    query: (id) => ({
      url: `${DATABLOCKS_URL}${id}/views/`,
      method: "GET",
    }),
  }),
  getDataBlockRequests: builder.query<
    DataBlockRequest[] | PaginatedResponse<DataBlockRequest>,
    DataBlockRequestQueryParams
  >({
    query: (queryParams) => ({
      url: createUrl(DATABLOCK_REQUESTS_URL, queryParams),
      method: "GET",
    }),
    providesTags: (result): TagType[] =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as DataBlockRequest[]
            ).map(
              (i): TagType => ({
                type: "DataBlockRequest",
                id: i.id,
              }),
            ),
            { type: "DataBlockRequests" },
          ]
        : [{ type: "DataBlockRequests" }],
  }),
  createDataBlockRequest: builder.mutation({
    query: (data) => ({
      url: DATABLOCK_REQUESTS_URL,
      method: "POST",
      data: data,
    }),
    invalidatesTags: [{ type: "DataBlockRequests" }],
  }),
  createBulkDataBlockRequests: builder.mutation({
    query: (data) => ({
      url: `${DATABLOCK_REQUESTS_URL}bulk/`,
      method: "POST",
      data: data,
    }),
    invalidatesTags: [{ type: "DataBlockRequests" }],
  }),
  getDataBlockRequestById: builder.query<
    DataBlockRequest,
    string | number | undefined
  >({
    query: (id) => ({
      url: `${DATABLOCK_REQUESTS_URL}${id}/`,
      method: "GET",
    }),
    providesTags: (result): TagType[] =>
      result
        ? [
            { type: "DataBlockRequest", id: result.id },
            { type: "DataBlockRequests" },
          ]
        : [{ type: "DataBlockRequests" }],
  }),
  deleteDataBlockRequest: builder.mutation({
    query: (id) => ({
      url: `${DATABLOCK_REQUESTS_URL}${id}/`,
      method: "DELETE",
    }),
    invalidatesTags: [{ type: "DataBlockRequests" }],
  }),
  requestDataBlockEdit: builder.mutation({
    query: ({ id, data }) => ({
      url: `${DATABLOCK_REQUESTS_URL}${id}/edit/`,
      method: "POST",
      data,
    }),
    invalidatesTags: [{ type: "DataBlockRequests" }, { type: "DataBlocks" }],
  }),
  patchDataBlockRequest: builder.mutation({
    query: ({ id, data }) => ({
      url: `${DATABLOCK_REQUESTS_URL}${id}/`,
      method: "PATCH",
      data,
    }),
    invalidatesTags: [{ type: "DataBlockRequests" }],
  }),

  getDataTemplates: builder.query<
    DataTemplateResponse[] | PaginatedResponse<DataTemplateResponse>,
    DataTemplatesQueryParams
  >({
    query: (queryParams) => ({
      url: createUrl(DATA_TEMPLATES_URL, queryParams),
      method: "GET",
    }),
    providesTags: (result) =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as DataTemplateResponse[]
            ).map(
              (template): TagType => ({
                type: "DataTemplate",
                id: template.id,
              }),
            ),
            { type: "DataTemplate", id: "PARTIAL-LIST" },
          ]
        : [{ type: "DataTemplates" }],
  }),
  getDataTemplateById: builder.query<DataTemplateResponse, number>({
    query: (id) => ({
      url: `${DATA_TEMPLATES_URL}${id}/`,
      method: "GET",
    }),
    providesTags: (result): TagType[] =>
      result
        ? [{ type: "DataTemplate", id: result.id }]
        : [{ type: "DataTemplates" }],
  }),

  getChoiceOptions: builder.query<
    ChoiceOptionResponse[] | PaginatedResponse<ChoiceOptionResponse>,
    ChoiceOptionsQueryParams
  >({
    query: (queryParams) => ({
      url: createUrl(DATACHOICES_URL, queryParams),
      method: "GET",
    }),
    providesTags: (result) =>
      result
        ? [
            ...(
              (isPaginatedResponse(result)
                ? result.results
                : result) as ChoiceOptionResponse[]
            ).map(
              (template): TagType => ({
                type: "ChoiceOption",
                id: template.id,
              }),
            ),
            { type: "ChoiceOptions" },
          ]
        : [{ type: "ChoiceOptions" }],
  }),
  getChoiceOptionById: builder.query<ChoiceOptionResponse, number>({
    query: (id) => ({
      url: `${DATACHOICES_URL}${id}/`,
      method: "GET",
    }),
    providesTags: (result): TagType[] =>
      result
        ? [{ type: "ChoiceOption", id: result.id }]
        : [{ type: "ChoiceOptions" }],
  }),
});

export default dataBlocksService;
